import CancelIcon from "@mui/icons-material/Cancel";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import { styled, Theme } from "@mui/material";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { skipToken } from "@reduxjs/toolkit/query";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import { MRTNoMemo } from "_shared/_views/tables/table";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import Individual from "marketplace/individuals/individual";
import ViewIndividualProfile from "marketplace/individuals/profiles/_views/individual-profile-container";
import IndividualProfile from "marketplace/individuals/profiles/individual-profile";
import {
  type MRT_ColumnFiltersState as ColumnFiltersState,
  createMRTColumnHelper,
  type MRT_TableOptions,
  type MRT_PaginationState as PaginationState,
  type MRT_Row as Row,
  MRT_ToggleGlobalFilterButton as ShowHideColumnsButton,
  type MRT_SortingState as SortingState,
  MRT_ToggleDensePaddingButton as ToggleDensePaddingButton,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton as ToggleGlobalFilterButton
} from "material-react-table";
import { enqueueSnackbar } from "notistack";
import React, { useCallback, useEffect, useMemo } from "react";
import { useLoaderData, useNavigate, useSearchParams } from "react-router";
import UserInformation from "users/information/user-information";
import { PageTab } from "users/networking/_views/network-page-tabs";
import {
  useAcceptNetworkInvitationMutation,
  useCancelNetworkInvitationMutation,
  useGetUserNetworkInvitationsQuery,
  useRejectNetworkInvitationMutation,
} from "users/networking/connections/invitations/_api/network-invitation-api";
import NetworkInvitationQuery, {
  NetworkInvitationFilterParameters,
  NetworkInvitationOrderParameters,
} from "users/networking/connections/invitations/_api/queries/network-invitation-query";
import NetworkInvitationInfo from "users/networking/connections/invitations/network-invitation-info";

const ToolbarButtonsContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  display: "flex",
  alignItems: "end",
  flexDirection: "column",
  gap: theme.spacing(1),
}));
const ToolbarButtons = styled("div")(() => ({
  display: "flex",
  alignItems: "end",
  flexWrap: "nowrap",
}));
const RowActionsContainer = styled("div")(() => ({
  whiteSpace: "nowrap",
}));
const TableTopToolbarContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
}));
const LowerTopToolbarContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "left",
  width: "100%",
}));
const TableTitle = styled(Typography)(({ theme }) => ({
  paddingLeft: theme.spacing(1),
}));
const RowTitle = styled(Typography)(({ theme }) => ({
  fontSize: "1.1em",
  fontWeight: "600",
  marginLeft: theme.spacing(1.5),
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));
const NameCellContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
}));
const FilterButtons = styled("div")(({ theme }) => ({
  marginTop: theme.spacing(1),
  "& > .MuiChip-root": {
    [theme.breakpoints.down("xl")]: {
      marginBottom: theme.spacing(0.5),
    },
    marginLeft: theme.spacing(0.5),
  },
}));

const columnHelper = createMRTColumnHelper<NetworkInvitationInfo>();

export default function PendingTab() {
  const handledRef = React.useRef<string | null>(null);

  const [globalFilter, setGlobalFilter] = React.useState("");
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  });

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const confirm = useConfirmDialog();
  const { openDialog } = useDialog();
  const isMediumDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));

  // Route
  const routeParams = useLoaderData();
  const routeAction = routeParams?.action as string | undefined;
  const routeId = routeParams?.id as string | undefined;
  const context = searchParams.get("context") ?? "received";
  const sent = context === "sent";
  const received = context === "received";

  // #region Queries
  const [acceptInvitation, acceptInvitationResult] = useAcceptNetworkInvitationMutation();
  const [cancelInvitation, cancelInvitationResult] = useCancelNetworkInvitationMutation();
  const [rejectInvitation, rejectInvitationResult] = useRejectNetworkInvitationMutation();

  const name = columnFilters.find((filter) => filter.id === "name")?.value as string;
  const status = columnFilters.find((filter) => filter.id === "status")?.value as string;
  const message = columnFilters.find((filter) => filter.id === "message")?.value as string;
  const sentDate = columnFilters.find((filter) => filter.id === "createdDate")?.value as string;

  const query = useMemo(() => {
    const paginationParameters = PaginationParameters.create(pagination);
    const filterParameters = new NetworkInvitationFilterParameters({
      name: name,
      status: status,
      message: message,
      sentDate: sentDate ? new AHDate(sentDate) : undefined,
    });

    const orderParameters = new NetworkInvitationOrderParameters();
    // (sorting[0]?.id as NetworkInvitationOrderField) ?? NetworkInvitationOrderField.Name,
    // sorting[0]?.desc === false ? OrderDirection.Ascending : OrderDirection.Descending

    return new NetworkInvitationQuery({
      filterParameters,
      orderParameters,
      paginationParameters,
      query: globalFilter,
    });
  }, [globalFilter, name, status, message, sentDate, pagination]);

  const networkInvitationQuery = useGetUserNetworkInvitationsQuery(query);

  const invitationUserIds = useMemo(() => {
    const queryResult = networkInvitationQuery.data;
    if (!queryResult) return new Set<string>();
    return new Set(queryResult.data.map((invitation) => invitation.invitee.id));
  }, [networkInvitationQuery.data]);

  const individualQuery = useGetIndividualsByUserIdsQuery(
    invitationUserIds?.size ? { userIds: invitationUserIds } : skipToken,
  );

  const networkInvitations = useMemo(() => {
    const queryResult = networkInvitationQuery.data;

    if (!queryResult) {
      return PaginatedResponse.empty<NetworkInvitationInfo>();
    }

    const data = queryResult.data.filter((invitation) => {
      if (invitation.status !== "Open") return false;
      const context = searchParams.get("context");
      if (context === "sent" && invitation.isInviter) return true;
      return context === "received" && !invitation.isInviter;
    });

    return PaginatedResponse.from<NetworkInvitationInfo>(
      NetworkInvitationInfo.fromJSONArray(data),
      queryResult.headers,
    );
  }, [networkInvitationQuery.data, searchParams]);

  const handleViewProfile = useCallback(
    (individualId?: Guid, data?: NetworkInvitationInfo): void => {
      if (!individualId) return;

      let individualProfile: IndividualProfile | undefined;
      if (data instanceof UserInformation) {
        individualProfile = new IndividualProfile({
          userId: data.id,
          firstName: data.name.firstName ?? "",
          lastName: data.name.lastName ?? "",
          avatarId: data.avatarId,
          legalEntityId: data.legalEntityId,
          email: data.email,
        });
      } else if (data instanceof NetworkInvitationInfo) {
        const context = searchParams.get("context");
        const profileToShow = context === "sent" ? data.invitee : data.inviter;
        individualProfile = new IndividualProfile({
          userId: profileToShow.id,
          firstName: profileToShow.name.firstName ?? "",
          lastName: profileToShow.name.lastName ?? "",
          // TODO: The backend does not currently return an avatarId, it is always null
          avatarId: data.avatarId,
          email: profileToShow.email ?? undefined,
        });
      }

      openDialog({
        titleStyle: {
          paddingBottom: 0,
        },
        contentSxProps: {
          display: "flex",
          overflowX: "hidden",
        },
        MuiProps: {
          maxWidth: "lg",
          fullWidth: false,
        },
        component: (
          <ViewIndividualProfile
            individualId={individualId}
            profile={individualProfile}
          />
        ),
        onClose: () => {
          const context = searchParams.get("context") ?? "received";
          navigate(`/network/pending?context=${context}`);
          handledRef.current = null;
        }
      });
    },
    [openDialog, navigate, searchParams]
  );

  // Handle URL changes
  useEffect(() => {    
    let context = searchParams.get("context");
    if (context === null) {
      setSearchParams((prevParams) => {
        const params = new URLSearchParams(prevParams);
        params.set("context", "received");
        return params;
      });
      return;
    }

    if (routeAction === "view" && routeId) {
      const key = `${routeAction}:${routeId}`;
      if (handledRef.current === key) return;

      const match = networkInvitations.data?.find(nc => nc.id?.value === routeId);
      if (!match?.inviter) return;

      handledRef.current = key;
      handleViewProfile(new Guid(match.inviter.id.value), match);
    }
  }, [handleViewProfile, networkInvitations.data, routeAction, routeId, searchParams, setSearchParams]);

  const renderPendingNameColumn = useCallback(
    (invitation: NetworkInvitationInfo) => {
      const user = invitation.isInviter ? invitation.invitee : invitation.inviter;
      const individualJson = individualQuery.data?.find((ind) => ind.userId === user?.id.value);
      const individual = Individual.fromJSON(individualJson);
      return (
        <NameCellContainer>
          <IndividualAvatar individual={individual ?? undefined} />
          <RowTitle>{user?.name.fullName}</RowTitle>
        </NameCellContainer>
      );
    },
    [individualQuery.data],
  );

  const pendingColumns = useMemo(
    () => [
      columnHelper.display({
        header: "Name",
        id: "name",
        Cell: ({ row }) => renderPendingNameColumn(row.original),
      }),
      columnHelper.display({
        header: "Sent",
        id: "createdDate",
        Cell: ({ row }) => {
          const created = row.original.created;
          if (created instanceof AHDate) return created.format("MM/DD/YYYY");
          return created;
        },
      }),
      columnHelper.display({
        header: "Message",
        id: "message",
        Cell: ({ row }) => {
          const message = row.original.message ?? <Icon>remove</Icon>;
          return message;
        },
      }),
      columnHelper.display({
        header: "Status",
        id: "status",
        Cell: ({ row }) => row.original.status,
      }),
    ],
    [renderPendingNameColumn],
  );

  async function handleAcceptInvitation(invitationId: Guid): Promise<void> {
    if (!invitationId) {
      await Promise.reject(new Error("No network invitation id provided"));
      return;
    }

    try {
      await acceptInvitation({ invitationId });

      if (acceptInvitationResult.isError) {
        throw acceptInvitationResult.error;
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to accept invitation", { variant: "error" });
    }
  }

  async function handleRejectInvitation(invitationId: Guid): Promise<void> {
    try {
      if (!invitationId) {
        throw new Error("No network invitation id provided");
      }

      await rejectInvitation({ invitationId });

      if (rejectInvitationResult.isError) {
        throw rejectInvitationResult.error;
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to reject invitation", { variant: "error" });
    }
  }

  async function handleCancelInvitation(invitationId: Guid): Promise<void> {
    try {
      if (!invitationId) {
        await Promise.reject(new Error("No network invitation id provided"));
        return;
      }

      const response = await confirm({
        title: "Cancel Network Connection Invitation?",
        message: "Are you sure you want to cancel this network connection invitation?",
        okButtonText: "Yes",
        cancelButtonText: "No",
      });

      if (response === ConfirmResponse.Cancel) return;

      await cancelInvitation({ invitationId });

      if (cancelInvitationResult.isError) {
        throw cancelInvitationResult.error;
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to cancel invitation", { variant: "error" });
    }
  }

  function getPendingTabActions(row: Row<NetworkInvitationInfo>) {
    const invitation = row.original;
    if (invitation?.status === "accepted" || invitation?.status === "rejected") {
      return null;
    }
    return (
      <>
        {invitation.isInviter ? (
          <Tooltip title="Cancel Invitation">
            <span>
              <IconButton
                onClick={(event) => {
                  event.stopPropagation();
                  void handleCancelInvitation(invitation.id);
                }}
              >
                <CancelIcon color="error" />
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          <>
            <Tooltip title="Accept Invitation">
              <span>
                <IconButton
                  onClick={(event) => {
                    event.stopPropagation();
                    void handleAcceptInvitation(invitation.id);
                  }}
                >
                  <CheckIcon color="success" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Reject Invitation">
              <span>
                <IconButton
                  onClick={(event) => {
                    event.stopPropagation();
                    void handleRejectInvitation(invitation.id);
                  }}
                >
                  <CloseIcon color="error" />
                </IconButton>
              </span>
            </Tooltip>
          </>
        )}
      </>
    );
  }

  const tableOptions: MRT_TableOptions<NetworkInvitationInfo> = {
    columns: pendingColumns,
    data: networkInvitations.data,
    enableRowSelection: false,
    enableTableHead: true,
    enableRowActions: true,
    enableColumnPinning: true,
    renderRowActions: ({ row }) => {
      return <RowActionsContainer>{getPendingTabActions(row)}</RowActionsContainer>;
    },
    renderTopToolbarCustomActions: ({ table }) => {
      return (
        <TableTopToolbarContainer>
          <TableTitle variant="h4">Network</TableTitle>
          <LowerTopToolbarContainer>
            <Tabs
              value={PageTab.Pending}
              indicatorColor="primary"
              textColor="primary"
              onChange={(_event, newValue) => {
                navigate(`/network/${newValue}`);
              }}
            >
              <Tab value={PageTab.Contacts} label="Contacts" />
              <Tab value={PageTab.Coworkers} label="Coworkers" />
              <Tab value={PageTab.Pending} label="Pending" />
              <Tab value={PageTab.Groups} label="Groups" />
            </Tabs>
            <FilterButtons>
              <Chip
                icon={<FilterListIcon />}
                label="Received"
                color="primary"
                variant={received ? "filled" : "outlined"}
                onClick={() => {
                  if (received) return;
                  setSearchParams((prevParams) => {
                    const params = new URLSearchParams(prevParams);
                    params.set("context", "received");
                    return params;
                  });
                }}
              />
              <Chip
                icon={<FilterListIcon />}
                label="Sent"
                color="primary"
                variant={sent ? "filled" : "outlined"}
                onClick={() => {
                  if (sent) return;
                  setSearchParams((prevParams) => {
                    const params = new URLSearchParams(prevParams);
                    params.set("context", "sent");
                    return params;
                  });
                }}
              />
            </FilterButtons>
          </LowerTopToolbarContainer>
          {isMediumDisplaySize && (
            <ToolbarButtonsContainer>
              <ToolbarButtons>
                <ToggleGlobalFilterButton table={table} />
                <ToggleFiltersButton table={table} />
                <ShowHideColumnsButton table={table} />
                <ToggleDensePaddingButton table={table} />
                <ToggleFullScreenButton table={table} />
              </ToolbarButtons>
            </ToolbarButtonsContainer>
          )}
        </TableTopToolbarContainer>
      );
    },
    initialState: {
      columnPinning: {
        right: ["mrt-row-actions"],
      },
    },
    manualFiltering: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    manualPagination: true,
    manualSorting: true,
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => {
        const context = searchParams.get("context") ?? "received";
        navigate(`/network/pending/view/${row.original.id}?context=${context}`);
      },
      sx: { cursor: "pointer" },
    }),
    muiTableContainerProps: () => ({
      sx: {
        flex: 1
      },
    }),
    muiTablePaperProps: {
      sx: {
        height: "100%",
        display: "flex",
        flexDirection: "column"
      },
    },
    muiToolbarAlertBannerProps: networkInvitationQuery.isError
      ? {
        color: "error",
        children: "Error loading data",
      }
      : undefined,
    rowCount: networkInvitations.totalElements,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    state: {
      globalFilter,
      columnFilters,
      isLoading: networkInvitationQuery.isLoading,
      pagination,
      showAlertBanner: networkInvitationQuery.isError,
      showProgressBars: networkInvitationQuery.isFetching,
      showSkeletons: networkInvitationQuery.isLoading,
      sorting,
    },
  }

  return <MRTNoMemo<NetworkInvitationInfo> options={tableOptions} />;
}
